import React, { useCallback, useMemo, useState } from "react";

import { faPlus, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { useOrganizationUsers } from "src/api/taktile/queries";
import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { useDatasets } from "src/datasets/api/queries";
import { toastActions } from "src/design-system/Toast/utils";
import {
  useDispatchLoadTestRunEvent,
  useDispatchStopTestRunEvent,
  useTestRunRuns,
} from "src/flowContainer/TestRunsSidePane/hooks";
import { TestRunsTable } from "src/flowContainer/TestRunsSidePane/TestRunsTable";
import { useStopRunV2 } from "src/jobs/api/queries";
import { JobRun } from "src/jobs/types";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";

type Props = {
  onRequestClose: () => void;
};

export const TestRunsSidePane: React.FC<Props> = ({ onRequestClose }) => {
  const { workspace, flow, version } = useAuthoringContext();
  const { data: users, isLoading: usersLoading } = useOrganizationUsers(
    workspace.organization_id,
  );
  const dispatchStopTestRunEvent = useDispatchStopTestRunEvent();
  const dispatchLoadJobRunEvent = useDispatchLoadTestRunEvent();

  const [loadedTestRunId, setLoadedTestRunId] = useState<string | null>(null);

  const {
    testRunRuns,
    isRunning,
    sourceIdToDatasetId,
    isLoading: isTestRunRunsLoading,
  } = useTestRunRuns(flow.id, version.id);
  const { mutateAsync: stopJobRun } = useStopRunV2(workspace.base_url!);

  const { data: datasets, isLoading: datasetsLoading } = useDatasets({
    flowId: flow.id,
    baseUrl: workspace.base_url,
  });
  // Memoed because it is used for column generation in a child
  const datasetNamesBySourceId = useMemo(() => {
    return Object.entries(sourceIdToDatasetId).reduce(
      (acc, [sourceId, datasetId]) => {
        const dataset = datasets?.find((dataset) => dataset.id === datasetId);
        if (dataset) {
          acc[sourceId] = dataset.name;
        }

        return acc;
      },
      {} as Record<string, string>,
    );
  }, [datasets, sourceIdToDatasetId]);

  const usernameById = useMemo(() => {
    return (
      users?.pages.flat().reduce(
        (acc, user) => {
          acc[user.id] = user.username ?? null;
          return acc;
        },
        {} as Record<string, string | null>,
      ) ?? {}
    );
  }, [users]);

  const isLoading =
    isTestRunRunsLoading ||
    datasetsLoading ||
    usersLoading ||
    testRunRuns === undefined;

  const hasTestRuns =
    testRunRuns && testRunRuns.length > 0 && !isTestRunRunsLoading;

  const handleCreate = () => {};

  const handleStop = useCallback(
    async (jobRun: JobRun) => {
      try {
        await stopJobRun({ jobId: jobRun.job_id, runId: jobRun.id });
        dispatchStopTestRunEvent("sidepane");
        toastActions.default({ title: "Test run stopped" });
      } catch (error) {
        logger.error(error);
        toastActions.failure({ title: "Failed to stop test run" });
      }
    },
    [stopJobRun, dispatchStopTestRunEvent],
  );

  const handleLoad = useCallback(
    (jobRun: JobRun) => {
      const datasetId = sourceIdToDatasetId[jobRun.source_id] ?? "";
      const dataset = datasets?.find((d) => d.id === datasetId);
      dispatchLoadJobRunEvent({
        jobRun,
        datasetId,
        datasetEtag: dataset?.etag ?? "",
      });
      setLoadedTestRunId(jobRun.id);
      toastActions.loading({ title: "Loading test run" });
    },
    [sourceIdToDatasetId, datasets, dispatchLoadJobRunEvent],
  );

  return (
    <div className="flex h-full w-128 flex-col bg-white py-6">
      <Header
        onCreate={handleCreate}
        isRunning={isRunning}
        onRequestClose={onRequestClose}
      />
      <div className="decideScrollbar mt-6 grow border-gray-100 border-t">
        <TestRunsTable
          lastUpdatedAt={version.updated_at}
          datasetNamesBySourceId={datasetNamesBySourceId ?? {}}
          loadedTestRunId={loadedTestRunId}
          isLoading={isLoading}
          jobRuns={testRunRuns ?? []}
          isEmpty={!hasTestRuns}
          usernameById={usernameById}
          onCreate={handleCreate}
          onLoad={handleLoad}
          onStop={handleStop}
        />
      </div>
    </div>
  );
};

type HeaderProps = {
  isRunning: boolean;
  onCreate: () => void;
  onRequestClose: () => void;
};

const Header: React.FC<HeaderProps> = ({
  onCreate,
  isRunning,
  onRequestClose,
}) => {
  return (
    <>
      <div>
        <div className="mb-2 flex items-start justify-around px-6">
          <div className="grow">
            <div className="mt-0.5 font-inter-semibold-16px text-gray-800">
              Test runs
            </div>
          </div>
          <div className="mt-1 flex flex-row items-center gap-x-2">
            <Button
              dataLoc="test-runs-create-test-run-button"
              disabled={isRunning}
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={onCreate}
            >
              New test run
            </Button>
            <Icon
              color="text-gray-500 hover:text-gray-700"
              dataLoc="close-test-runs-side-pane"
              icon={faXmark}
              size="xs"
              onClick={onRequestClose}
            />
          </div>
        </div>
      </div>
      <span className="px-6 font-inter-normal-12px text-gray-500">
        Create test runs to verify your decision logic. Note that history
        viewing is currently supported for AI test runs only.
      </span>
    </>
  );
};
